// Analysis Data Service - Aggregates student data from multiple sources
import {
  getUserTestResults,
  getUserProfile,
  getDoubts,
  getUserWrongQuestions,
  getUserPracticeSessions,
  getUserRevisionDataByUserId
} from '../firebase/collections';
import {
  getUserTestResults as getUserTestResultsSubcollection,
  getUserNotes,
  getUserDoubts,
  getUserProgress,
  getUserActivities,
  getUserEnrollments,
  getUserSettings,
  getDailyTodos
} from '../firebase/subcollections';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Aggregates all student data for analysis
 * @param {string} userId - Student's user ID
 * @param {Array} batchIds - Array of batch IDs student is enrolled in
 * @param {Object} timeRange - Time range for analysis {start, end}
 * @returns {Promise<Object>} Aggregated student data
 */
export async function aggregateStudentData(userId, batchIds = [], timeRange = null) {
  try {
    // Set default time range if not provided (last 6 months)
    const defaultTimeRange = {
      start: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000),
      end: new Date()
    };
    const range = timeRange || defaultTimeRange;

    // Fetch all data in parallel
    const [
      testData,
      assignmentData,
      attendanceData,
      doubtData,
      revisionData,
      studyStreakData,
      todoTasksData,
      courseData,
      counsellingData,
      profileSettings,
      bookmarksData
    ] = await Promise.all([
      fetchTestPerformanceData(userId, range),
      fetchAssignmentData(userId, batchIds, range),
      fetchStudentAttendanceData(userId, batchIds, range),
      fetchDoubtData(userId, batchIds, range),
      fetchRevisionData(userId, range),
      fetchStudyStreakData(userId),
      fetchTodoTasksData(userId, range),
      fetchEnrolledCoursesData(userId),
      fetchCounsellingData(userId, range),
      fetchUserProfileSettings(userId),
      fetchBookmarksData(userId, range)
    ]);

    return {
      userId,
      generatedAt: new Date(),
      timeRange: range,
      testData,
      assignmentData,
      attendanceData,
      doubtData,
      revisionData,
      studyStreakData,
      todoTasksData,
      courseData,
      counsellingData,
      profileSettings,
      bookmarksData
    };
  } catch (error) {
    console.error('Error aggregating student data:', error);
    throw error;
  }
}

/**
 * Fetches test performance data
 */
export async function fetchTestPerformanceData(userId, timeRange) {
  try {
    const result = await getUserTestResults(userId, 1000);
    
    if (!result.success) {
      return { testResults: [], error: result.error };
    }

    // Filter by time range
    const filteredResults = result.data.filter(test => {
      const testDate = new Date(test.completedAt || test.createdAt);
      return testDate >= timeRange.start && testDate <= timeRange.end;
    });

    return {
      testResults: filteredResults,
      totalTests: filteredResults.length,
      hasData: filteredResults.length > 0
    };
  } catch (error) {
    console.error('Error fetching test data:', error);
    return { testResults: [], error: error.message, hasData: false };
  }
}

/**
 * Fetches assignment submission data
 */
export async function fetchAssignmentData(userId, batchIds, timeRange) {
  try {
    const assignments = [];
    
    // Query assignment submissions for each batch
    for (const batchId of batchIds) {
      const q = query(
        collection(db, 'assignmentSubmissions'),
        where('studentId', '==', userId),
        where('batchId', '==', batchId)
      );
      
      const snapshot = await getDocs(q);
      snapshot.forEach(doc => {
        const data = doc.data();
        const submittedAt = data.submittedAt?.toDate?.() || new Date(data.submittedAt);
        
        if (submittedAt >= timeRange.start && submittedAt <= timeRange.end) {
          assignments.push({ id: doc.id, ...data });
        }
      });
    }

    return {
      submissions: assignments,
      totalAssignments: assignments.length,
      hasData: assignments.length > 0
    };
  } catch (error) {
    console.error('Error fetching assignment data:', error);
    return { submissions: [], error: error.message, hasData: false };
  }
}

/**
 * Fetches student attendance data (not teacher attendance)
 */
export async function fetchStudentAttendanceData(userId, batchIds, timeRange) {
  try {
    const attendanceRecords = [];
    
    // Query batch enrollments to get attendance data
    for (const batchId of batchIds) {
      const q = query(
        collection(db, 'batchEnrollments'),
        where('userId', '==', userId),
        where('batchId', '==', batchId)
      );
      
      const snapshot = await getDocs(q);
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.attendance) {
          attendanceRecords.push({
            batchId,
            batchName: data.batchName || 'Unknown Batch',
            attendance: data.attendance
          });
        }
      });
    }

    return {
      records: attendanceRecords,
      hasData: attendanceRecords.length > 0
    };
  } catch (error) {
    console.error('Error fetching attendance data:', error);
    return { records: [], error: error.message, hasData: false };
  }
}

/**
 * Fetches doubt activity data from student's enrolled batches
 */
export async function fetchDoubtData(userId, batchIds, timeRange) {
  try {
    const allDoubts = [];
    
    // Fetch doubts from main doubts collection by authorId
    const doubtsQuery = query(
      collection(db, 'doubts'),
      where('authorId', '==', userId)
    );
    
    const doubtsSnapshot = await getDocs(doubtsQuery);
    doubtsSnapshot.forEach(doc => {
      allDoubts.push({ id: doc.id, ...doc.data() });
    });

    // Fetch doubts from each batch the student is enrolled in
    for (const batchId of batchIds) {
      try {
        const batchDoubtsQuery = query(
          collection(db, 'doubts'),
          where('batchId', '==', batchId),
          where('authorId', '==', userId)
        );
        
        const batchDoubtsSnapshot = await getDocs(batchDoubtsQuery);
        batchDoubtsSnapshot.forEach(doc => {
          allDoubts.push({ id: doc.id, ...doc.data() });
        });
      } catch (batchError) {
        console.log(`Error fetching doubts for batch ${batchId}:`, batchError.message);
      }
    }

    // Also try to fetch from user subcollection
    try {
      const userDoubtsResult = await getUserDoubts(userId, 1000);
      if (userDoubtsResult.success && userDoubtsResult.data.length > 0) {
        allDoubts.push(...userDoubtsResult.data);
      }
    } catch (subError) {
      console.log('User subcollection doubts not available:', subError.message);
    }

    // Remove duplicates based on ID
    const uniqueDoubts = Array.from(new Map(allDoubts.map(d => [d.id, d])).values());

    // Filter by time range
    const filteredDoubts = uniqueDoubts.filter(doubt => {
      const doubtDate = doubt.createdAt?.toDate?.() || new Date(doubt.createdAt);
      return doubtDate >= timeRange.start && doubtDate <= timeRange.end;
    });

    return {
      doubts: filteredDoubts,
      totalDoubts: filteredDoubts.length,
      hasData: filteredDoubts.length > 0
    };
  } catch (error) {
    console.error('Error fetching doubt data:', error);
    return { doubts: [], error: error.message, hasData: false };
  }
}

/**
 * Fetches revision and practice session data
 */
export async function fetchRevisionData(userId, timeRange) {
  try {
    console.log('Fetching revision data for user:', userId);
    
    // Fetch from multiple sources with individual error handling
    let wrongQuestions = [];
    let practiceSessions = [];
    let revisionData = null;
    let progressData = [];

    // Fetch wrong questions
    try {
      const wrongQuestionsResult = await getUserWrongQuestions(userId, 1000);
      console.log('Wrong questions result:', wrongQuestionsResult);
      if (wrongQuestionsResult.success && wrongQuestionsResult.data) {
        wrongQuestions = wrongQuestionsResult.data;
      }
    } catch (error) {
      console.error('Error fetching wrong questions:', error);
    }

    // Fetch practice sessions
    try {
      const practiceSessionsResult = await getUserPracticeSessions(userId, 1000);
      console.log('Practice sessions result:', practiceSessionsResult);
      if (practiceSessionsResult.success && practiceSessionsResult.data) {
        practiceSessions = practiceSessionsResult.data;
      }
    } catch (error) {
      console.error('Error fetching practice sessions:', error);
    }

    // Fetch revision data
    try {
      const revisionDataResult = await getUserRevisionDataByUserId(userId);
      console.log('Revision data result:', revisionDataResult);
      if (revisionDataResult.success && revisionDataResult.data) {
        revisionData = revisionDataResult.data;
      }
    } catch (error) {
      console.error('Error fetching revision data:', error);
    }

    // Fetch progress data
    try {
      const progressResult = await getUserProgress(userId, 1000);
      console.log('Progress result:', progressResult);
      if (progressResult.success && progressResult.data) {
        progressData = progressResult.data;
      }
    } catch (error) {
      console.error('Error fetching progress data:', error);
    }

    // Combine practice sessions from progress data
    const allSessions = [...practiceSessions];
    
    // Add progress entries that look like practice sessions
    progressData.forEach(progress => {
      if (progress.type === 'practice' || progress.type === 'revision') {
        allSessions.push({
          id: progress.id,
          createdAt: progress.createdAt || progress.updatedAt,
          topic: progress.topic || 'General',
          questionCount: progress.questionsAttempted || 0,
          accuracy: progress.accuracy || 0,
          timeSpent: progress.timeSpent || 0
        });
      }
    });

    // Filter wrong questions by time range
    const filteredWrongQuestions = wrongQuestions.filter(q => {
      try {
        const qDate = q.createdAt?.toDate?.() || new Date(q.createdAt);
        return qDate >= timeRange.start && qDate <= timeRange.end;
      } catch (e) {
        return false;
      }
    });

    // Count attempts per unique question
    const questionAttempts = {};
    filteredWrongQuestions.forEach(q => {
      const questionId = q.questionId || q.id;
      if (!questionAttempts[questionId]) {
        questionAttempts[questionId] = {
          question: q,
          attempts: 0,
          wrongCount: 0,
          skippedCount: 0,
          lastAttempt: q.createdAt
        };
      }
      questionAttempts[questionId].attempts++;
      if (q.status === 'wrong' || q.isWrong) {
        questionAttempts[questionId].wrongCount++;
      } else if (q.status === 'skipped' || q.isSkipped) {
        questionAttempts[questionId].skippedCount++;
      }
      // Keep the most recent attempt
      const currentDate = q.createdAt?.toDate?.() || new Date(q.createdAt);
      const lastDate = questionAttempts[questionId].lastAttempt?.toDate?.() || new Date(questionAttempts[questionId].lastAttempt);
      if (currentDate > lastDate) {
        questionAttempts[questionId].question = q;
        questionAttempts[questionId].lastAttempt = q.createdAt;
      }
    });

    // Convert to array with attempt counts
    const questionsWithAttempts = Object.values(questionAttempts).map(qa => ({
      ...qa.question,
      attempts: qa.attempts,
      wrongCount: qa.wrongCount,
      skippedCount: qa.skippedCount
    }));

    // Count unique wrong and skipped questions
    // Note: A question can be both wrong AND skipped in different attempts
    const uniqueWrongQuestions = questionsWithAttempts.filter(q => q.wrongCount > 0).length;
    const uniqueSkippedQuestions = questionsWithAttempts.filter(q => q.skippedCount > 0).length;
    
    // Also count questions that are ONLY wrong or ONLY skipped (not both)
    const onlyWrong = questionsWithAttempts.filter(q => q.wrongCount > 0 && q.skippedCount === 0).length;
    const onlySkipped = questionsWithAttempts.filter(q => q.skippedCount > 0 && q.wrongCount === 0).length;
    const bothWrongAndSkipped = questionsWithAttempts.filter(q => q.wrongCount > 0 && q.skippedCount > 0).length;

    console.log('Revision data filtering summary:', {
      totalWrongQuestionsBeforeFilter: wrongQuestions.length,
      filteredWrongQuestions: filteredWrongQuestions.length,
      uniqueWrongQuestions,
      uniqueSkippedQuestions,
      onlyWrong,
      onlySkipped,
      bothWrongAndSkipped,
      totalUniqueQuestions: questionsWithAttempts.length,
      timeRange: {
        start: timeRange.start.toISOString(),
        end: timeRange.end.toISOString()
      }
    });

    // Convert wrong/skipped questions into practice sessions for analysis
    // Group by date and topic to create meaningful sessions
    const questionsByDateTopic = {};
    filteredWrongQuestions.forEach(q => {
      const date = q.createdAt?.toDate?.() || new Date(q.createdAt);
      const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
      const topic = q.topic || q.subject || 'General';
      const key = `${dateKey}-${topic}`;
      
      if (!questionsByDateTopic[key]) {
        questionsByDateTopic[key] = {
          date: date,
          topic: topic,
          questions: [],
          wrongCount: 0,
          skippedCount: 0
        };
      }
      
      questionsByDateTopic[key].questions.push(q);
      if (q.status === 'wrong' || q.isWrong) {
        questionsByDateTopic[key].wrongCount++;
      } else if (q.status === 'skipped' || q.isSkipped) {
        questionsByDateTopic[key].skippedCount++;
      }
    });

    // Create synthetic practice sessions from wrong/skipped questions
    Object.values(questionsByDateTopic).forEach(group => {
      allSessions.push({
        id: `revision-${group.date.getTime()}-${group.topic}`,
        createdAt: group.date,
        topic: group.topic,
        questionCount: group.questions.length,
        wrongCount: group.wrongCount,
        skippedCount: group.skippedCount,
        accuracy: 0, // These are wrong/skipped, so 0% accuracy
        timeSpent: group.questions.length * 2, // Estimate 2 minutes per question
        isRevisionSession: true
      });
    });

    // Filter all sessions by time range
    const filteredSessions = allSessions.filter(session => {
      try {
        const sessionDate = session.createdAt?.toDate?.() || new Date(session.createdAt);
        return sessionDate >= timeRange.start && sessionDate <= timeRange.end;
      } catch (e) {
        return false;
      }
    });

    console.log('Revision data summary:', {
      wrongQuestions: filteredWrongQuestions.length,
      practiceSessions: filteredSessions.length,
      syntheticSessions: Object.keys(questionsByDateTopic).length,
      hasRevisionData: !!revisionData
    });

    return {
      wrongQuestions: filteredWrongQuestions,
      questionsWithAttempts: questionsWithAttempts,
      uniqueWrongQuestions: uniqueWrongQuestions,
      uniqueSkippedQuestions: uniqueSkippedQuestions,
      practiceSessions: filteredSessions,
      revisionData,
      hasData: filteredWrongQuestions.length > 0 || filteredSessions.length > 0
    };
  } catch (error) {
    console.error('Error fetching revision data:', error);
    return { wrongQuestions: [], practiceSessions: [], revisionData: null, error: error.message, hasData: false };
  }
}

/**
 * Fetches study streak data
 */
export async function fetchStudyStreakData(userId) {
  try {
    // Fetch streak data from the correct location: users/{userId}/streakData/currentStreak
    const streakDocRef = doc(db, 'users', userId, 'streakData', 'currentStreak');
    const streakDoc = await getDoc(streakDocRef);
    
    let streakData = {
      currentStreak: 0,
      longestStreak: 0,
      totalActiveDays: 0,
      lastActivityDate: null
    };
    
    if (streakDoc.exists()) {
      streakData = streakDoc.data();
    }
    
    // Get activities to calculate average study duration
    const activitiesResult = await getUserActivities(userId);
    const activities = activitiesResult.success ? activitiesResult.data : [];
    
    // Calculate average study duration from recent activities
    const recentActivities = activities.slice(0, 30); // Last 30 activities
    const totalDuration = recentActivities.reduce((sum, activity) => {
      return sum + (activity.duration || 30); // Default 30 minutes if not specified
    }, 0);
    const averageStudyDuration = recentActivities.length > 0 
      ? Math.round(totalDuration / recentActivities.length) 
      : 45; // Default 45 minutes
    
    // Determine streak pattern
    const currentStreak = streakData.currentStreak || 0;
    let streakPattern = 'irregular';
    if (currentStreak >= 14) streakPattern = 'consistent';
    else if (currentStreak >= 3) streakPattern = 'building';
    
    // Calculate motivation level (0-100)
    const motivationLevel = Math.min(100, currentStreak * 8);
    
    return {
      currentStreak: currentStreak,
      longestStreak: streakData.longestStreak || 0,
      lastStudyDate: streakData.lastActivityDate || null,
      streakPattern: streakPattern,
      averageStudyDuration: averageStudyDuration,
      motivationLevel: motivationLevel,
      totalActiveDays: streakData.totalActiveDays || 0,
      streakBreaks: [], // Can be calculated from activities if needed
      hasData: currentStreak > 0 || activities.length > 0
    };
  } catch (error) {
    console.error('Error fetching study streak data:', error);
    return { 
      currentStreak: 0, 
      longestStreak: 0, 
      streakPattern: 'irregular',
      averageStudyDuration: 0,
      motivationLevel: 0,
      error: error.message, 
      hasData: false 
    };
  }
}

/**
 * Fetches daily todo tasks
 */
export async function fetchTodoTasksData(userId, timeRange) {
  try {
    const result = await getDailyTodos(userId);
    
    if (!result.success) {
      return { tasks: [], error: result.error, hasData: false };
    }

    // Filter by time range
    const filteredTasks = result.data.filter(task => {
      const taskDate = task.createdAt?.toDate?.() || new Date(task.createdAt);
      return taskDate >= timeRange.start && taskDate <= timeRange.end;
    });

    return {
      tasks: filteredTasks,
      totalTasks: filteredTasks.length,
      hasData: filteredTasks.length > 0
    };
  } catch (error) {
    console.error('Error fetching todo tasks:', error);
    return { tasks: [], error: error.message, hasData: false };
  }
}

/**
 * Fetches enrolled courses and batch data
 */
export async function fetchEnrolledCoursesData(userId) {
  try {
    const result = await getUserEnrollments(userId);
    
    if (!result.success) {
      return { courses: [], batches: [], error: result.error, hasData: false };
    }

    // Also fetch batch enrollment details for additional context
    const batchEnrollments = [];
    const enrollments = result.data || [];
    
    for (const enrollment of enrollments) {
      if (enrollment.batchId) {
        try {
          // Get batch details
          const batchDoc = await getDoc(doc(db, 'batches', enrollment.batchId));
          if (batchDoc.exists()) {
            batchEnrollments.push({
              ...enrollment,
              batchDetails: batchDoc.data()
            });
          }
        } catch (batchError) {
          console.log(`Error fetching batch details for ${enrollment.batchId}:`, batchError.message);
        }
      }
    }

    return {
      courses: enrollments,
      batches: batchEnrollments,
      totalCourses: enrollments.length,
      hasData: enrollments.length > 0
    };
  } catch (error) {
    console.error('Error fetching course data:', error);
    return { courses: [], batches: [], error: error.message, hasData: false };
  }
}

/**
 * Fetches counselling session data
 */
export async function fetchCounsellingData(userId, timeRange) {
  try {
    // Query counselling sessions (assuming collection exists)
    const q = query(
      collection(db, 'counsellingSessions'),
      where('studentId', '==', userId),
      orderBy('sessionDate', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const sessions = [];
    
    snapshot.forEach(doc => {
      const data = doc.data();
      const sessionDate = data.sessionDate?.toDate?.() || new Date(data.sessionDate);
      
      if (sessionDate >= timeRange.start && sessionDate <= timeRange.end) {
        sessions.push({ id: doc.id, ...data });
      }
    });

    return {
      sessions,
      totalSessions: sessions.length,
      hasData: sessions.length > 0
    };
  } catch (error) {
    // Counselling collection might not exist, return empty data
    console.log('Counselling data not available:', error.message);
    return { sessions: [], totalSessions: 0, hasData: false };
  }
}

/**
 * Fetches user profile settings
 */
export async function fetchUserProfileSettings(userId) {
  try {
    // Fetch from multiple sources
    const [profileResult, settingsResult, studentProfileResult] = await Promise.all([
      getUserProfile(userId),
      getUserSettings(userId),
      fetchStudentProfile(userId)
    ]);

    const userProfile = profileResult.success ? profileResult.data : {};
    const settings = settingsResult.success ? settingsResult.data : {};
    const studentProfile = studentProfileResult.success ? studentProfileResult.data : {};

    // Merge all profile data
    const mergedProfile = {
      ...userProfile,
      ...studentProfile,
      // Keep user preferences from userProfile
      learningPreferences: userProfile.learningPreferences || studentProfile.learningPreferences,
      studyHabits: userProfile.studyHabits || studentProfile.studyHabits,
      preferredStudyTime: userProfile.preferredStudyTime || studentProfile.preferredStudyTime,
      goals: userProfile.goals || studentProfile.goals,
      strengths: userProfile.strengths || studentProfile.strengths,
      weaknesses: userProfile.weaknesses || studentProfile.weaknesses,
      interests: userProfile.interests || studentProfile.interests
    };

    console.log('Fetched profile data:', {
      hasUserProfile: Object.keys(userProfile).length > 0,
      hasStudentProfile: Object.keys(studentProfile).length > 0,
      hasSettings: Object.keys(settings).length > 0,
      mergedKeys: Object.keys(mergedProfile)
    });

    return {
      profile: mergedProfile,
      settings,
      hasData: Object.keys(mergedProfile).length > 0 || Object.keys(settings).length > 0
    };
  } catch (error) {
    console.error('Error fetching profile settings:', error);
    return { profile: {}, settings: {}, error: error.message, hasData: false };
  }
}

/**
 * Fetches student profile from studentProfiles collection
 */
async function fetchStudentProfile(userId) {
  try {
    const q = query(
      collection(db, 'studentProfiles'),
      where('studentId', '==', userId)
    );
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const data = snapshot.docs[0].data();
      return { success: true, data };
    }
    
    return { success: false, error: 'Student profile not found' };
  } catch (error) {
    console.error('Error fetching student profile:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Fetches bookmarks data
 */
export async function fetchBookmarksData(userId, timeRange) {
  try {
    // Query both bookmarks and question bookmarks
    const [bookmarksQuery, questionBookmarksQuery] = await Promise.all([
      getDocs(query(
        collection(db, 'bookmarks'),
        where('userId', '==', userId)
      )),
      getDocs(query(
        collection(db, 'questionBookmarks'),
        where('userId', '==', userId)
      ))
    ]);

    const bookmarks = [];
    const questionBookmarks = [];

    bookmarksQuery.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
      
      if (createdAt >= timeRange.start && createdAt <= timeRange.end) {
        bookmarks.push({ id: doc.id, ...data });
      }
    });

    questionBookmarksQuery.forEach(doc => {
      const data = doc.data();
      const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
      
      if (createdAt >= timeRange.start && createdAt <= timeRange.end) {
        questionBookmarks.push({ id: doc.id, ...data });
      }
    });

    return {
      bookmarks,
      questionBookmarks,
      totalBookmarks: bookmarks.length + questionBookmarks.length,
      hasData: bookmarks.length > 0 || questionBookmarks.length > 0
    };
  } catch (error) {
    console.error('Error fetching bookmarks:', error);
    return { bookmarks: [], questionBookmarks: [], totalBookmarks: 0, error: error.message, hasData: false };
  }
}
